/**
 * The controls a manager applies to one client, and the country list behind them.
 *
 * Four APIs shipped without a screen, so every one of these actions needed a
 * developer with database access — the opposite of the segregation of duties they
 * exist to enforce. They share this service because they share an audience and a
 * question: what is frozen for this client, and what may be unfrozen.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export interface SecurityHoldEvent {
  action: string;
  trigger: string | null;
  actor_id: string | null;
  note: string | null;
  created_at: string;
}

export interface SecurityHoldStatus {
  on_hold: boolean;
  held_at: string | null;
  /** What the client said was not theirs — a bank account added, so far. */
  trigger: string | null;
  events: SecurityHoldEvent[];
}

export type DestinationExceptionStatus = 'proposed' | 'approved' | 'rejected' | 'expired';

export interface DestinationException {
  id: string;
  client_bank_account_id: string;
  reason: string;
  status: DestinationExceptionStatus;
  proposed_by: string;
  proposed_at: string;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  expires_at: string | null;
}

export interface DestinationExceptions {
  /** The last day the 180-day return-to-source rule still binds this client. */
  window_ends_on: string | null;
  exceptions: DestinationException[];
}

/** One immutable classification: the band, the score, and the factors behind them. */
export interface RiskAssessment {
  id: string;
  risk_band: string;
  risk_score: number;
  /** The inputs the band was computed from — the answer to "why this band". */
  factors: Record<string, unknown>;
  trigger: string;
  source_case_id: string | null;
  notes: string | null;
  assessed_by: string;
  created_at: string;
}

export interface ClientRiskHistory {
  client_id: string;
  current: RiskAssessment | null;
  /** Append-only. This is the CNBV answer to "why is this client high risk". */
  history: RiskAssessment[];
}

export interface BlockedCountry {
  country_code: string;
  country_name: string;
  source: string;
  source_list: string | null;
  source_updated_at: string | null;
  first_blocked_at: string;
  last_verified_at: string | null;
  is_active: boolean;
  /** Set when the partner stopped listing it. It stays blocked until a
   *  super_admin lifts it by hand — a job never re-opens a country. */
  removal_pending_since: string | null;
  lifted_at: string | null;
  lift_reason: string | null;
}

export interface AddCountryBody {
  country_code: string;
  country_name: string;
  reason: string;
  source_url: string | null;
  mfa_code: string;
}

export interface BlockedCountrySyncRun {
  ran_at: string;
  status: string;
  source_updated_at: string | null;
  parsed_count: number | null;
  added: number | null;
  removal_pending: number | null;
}

export interface BlockedCountriesAdmin {
  countries: BlockedCountry[];
  recent_runs: BlockedCountrySyncRun[];
}

class ClientRiskService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  // ── Security hold (0079) ────────────────────────────────────────────
  async securityHold(clientId: string): Promise<SecurityHoldStatus> {
    const res = await this.api.get<SecurityHoldStatus>(
      `/api/v1/ops/clients/${clientId}/security-hold`,
    );
    return res.data;
  }

  /** Only a manager lifts a hold, and only after verifying the holder out of
   *  band — the note is that verification, and it is kept. */
  async liftSecurityHold(clientId: string, note: string, mfaCode: string): Promise<void> {
    await this.api.post(`/api/v1/ops/clients/${clientId}/security-hold/lift`, {
      note,
      mfa_code: mfaCode,
    });
  }

  // ── Destination exceptions (0083) ───────────────────────────────────
  async destinationExceptions(clientId: string): Promise<DestinationExceptions> {
    const res = await this.api.get<DestinationExceptions>(
      `/api/v1/ops/clients/${clientId}/destination-exceptions`,
    );
    return res.data;
  }

  async proposeException(
    clientId: string,
    clientBankAccountId: string,
    reason: string,
  ): Promise<void> {
    await this.api.post(`/api/v1/ops/clients/${clientId}/destination-exceptions`, {
      client_bank_account_id: clientBankAccountId,
      reason,
    });
  }

  /** The decider must not be the proposer — the server enforces it, and a DB
   *  CHECK enforces it again. */
  async decideException(
    exceptionId: string,
    approve: boolean,
    note: string,
    mfaCode: string,
  ): Promise<void> {
    await this.api.post(`/api/v1/ops/destination-exceptions/${exceptionId}/decision`, {
      approve,
      note,
      mfa_code: mfaCode,
    });
  }

  // ── Risk classification history ─────────────────────────────────────
  /** The current band AND the history behind it. A band with no reasoning is
   *  not an answer an examiner accepts. */
  async riskHistory(clientId: string): Promise<ClientRiskHistory> {
    const res = await this.api.get<ClientRiskHistory>(
      `/api/v1/manager/clients/${clientId}/risk`,
    );
    return res.data;
  }

  // ── Blocked countries (0081) ────────────────────────────────────────
  async blockedCountries(): Promise<BlockedCountriesAdmin> {
    const res = await this.api.get<BlockedCountriesAdmin>('/api/v1/admin/blocked-countries');
    return res.data;
  }

  /** super_admin only: block a country as QMI's own decision (every scope). */
  async addCountry(body: AddCountryBody): Promise<void> {
    await this.api.post('/api/v1/admin/blocked-countries', body);
  }

  /** super_admin only. Nothing else re-opens a country: the weekly sync adds
   *  automatically and never removes. */
  async liftCountry(countryCode: string, reason: string, mfaCode: string): Promise<void> {
    await this.api.post(`/api/v1/admin/blocked-countries/${countryCode}/lift`, {
      reason,
      mfa_code: mfaCode,
    });
  }
}

export default new ClientRiskService();
